import React, { useState, useEffect } from 'react';
import { Testimonial } from '../types';

interface Props {
  testimonials: Testimonial[];
}

export default function TestimonialCarousel({ testimonials }: Props) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Auto-advance logic
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (isAutoPlaying) {
      interval = setInterval(() => {
        nextSlide();
      }, 6000);
    }
    return () => clearInterval(interval);
  }, [activeIndex, isAutoPlaying]);

  const nextSlide = () => {
    setActiveIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToSlide = (index: number) => {
    setActiveIndex(index);
  };

  return (
    <div 
      className="relative w-full max-w-5xl mx-auto px-4 md:px-12"
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
    >
      {/* Cards Container */}
      <div className="overflow-hidden relative min-h-[300px] md:min-h-[250px]">
        <div 
          className="flex transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${activeIndex * 100}%)` }}
        >
          {testimonials.map((t) => (
            <div key={t.id} className="w-full flex-shrink-0 px-2 md:px-12">
              <div className="bg-suncoast-offWhite p-8 md:p-10 rounded-xl border-l-4 border-suncoast-accent shadow-sm h-full flex flex-col justify-center">
                <div className="mb-6">
                  <svg className="w-8 h-8 text-suncoast-secondary opacity-30 mb-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14.017 21L14.017 18C14.017 16.896 14.325 15.98 14.941 15.252C15.557 14.524 16.591 14.16 18.043 14.16V11.496C17.063 11.496 16.293 11.832 15.733 12.504C15.173 13.176 14.893 14.128 14.893 15.36V21H14.017ZM8 21L8 18C8 16.896 8.308 15.98 8.924 15.252C9.54 14.524 10.574 14.16 12.026 14.16V11.496C11.046 11.496 10.276 11.832 9.716 12.504C9.156 13.176 8.876 14.128 8.876 15.36V21H8Z" />
                  </svg>
                  <p className="text-xl md:text-2xl text-gray-700 italic font-light leading-relaxed">
                    "{t.quote}"
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-suncoast-primary text-white flex items-center justify-center font-bold text-lg">
                    {t.author.charAt(0)}
                  </div>
                  <div>
                    <div className="font-bold text-suncoast-dark text-lg">{t.author}</div>
                    <div className="text-sm text-gray-500 font-medium uppercase tracking-wide">{t.role}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Controls */}
      <button 
        onClick={prevSlide}
        className="absolute left-0 top-1/2 -translate-y-1/2 p-2 text-suncoast-primary hover:text-suncoast-accent transition-colors md:-ml-6"
        aria-label="Previous testimonial"
      >
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
      </button>

      <button 
        onClick={nextSlide}
        className="absolute right-0 top-1/2 -translate-y-1/2 p-2 text-suncoast-primary hover:text-suncoast-accent transition-colors md:-mr-6"
        aria-label="Next testimonial"
      >
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
      </button>

      {/* Dots */}
      <div className="flex justify-center gap-2 mt-8">
        {testimonials.map((_, idx) => (
          <button
            key={idx}
            onClick={() => goToSlide(idx)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              idx === activeIndex ? 'bg-suncoast-primary w-8' : 'bg-gray-300 hover:bg-suncoast-secondary'
            }`}
            aria-label={`Go to slide ${idx + 1}`}
          />
        ))}
      </div>
    </div>
  );
}